// @flow

import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  withTranslation, Trans,
} from '@i18n';
import { createSecondOpinionPatient, resetCandidateCreate } from '@redux/components/CandidateCreate/actions';
import type { GlobalState } from '@globalTypes';
import { getInternationalPhone } from '@services/phone';
import { loadFormValues } from '@lib/flowConstant';
import Grid from 'styled-components-grid';
import { withFormik } from '@lib';
import { FastField, Field } from 'formik';
import InputPhone from '@components/InputPhone';
import ButtonRadio from '@components/ButtonRadio';
import Checkbox from '@components/Checkbox';
import compose from 'lodash/fp/compose';
import Input from '@components/Input';
import { isEmpty } from 'lodash';
import { PhoneCode } from '@components/ContactPageNew/countryCode';
import { StyledLabelNew } from '@styles/labels';
import { StyledInput } from '@styles/inputs';
import { phone } from 'phone';

import {
  StyledMiniFlowWrapper,
  StyledContentTop,
  StyledFormGrid,
  StyledRadioGroup,
  StyledItem,
  TermsAndConditionsWrapper,
  StyledLabelTextWrapper,
  StyledButtonSubmit,
  StyledItemSpacing,
  StyledFormHeading,
  StyledFirstName,
  StyledLastName,
  StyledName,
  StyledPhone,
  StyledValidationMessage
} from './styles';

const ConsultationForm = ({
  t,
  formId,
  formName,
  setFieldValue,
  values,
  isModal,
  sending,
  handleSubmit,
  curefyDomain
}) => {
  const [checkboxChecked, setCheckboxChecked] = useState(true);
  const formDoctor = true;
  useEffect(() => {
    fetch('https://ipapi.co/json')
      .then(res => res.json())
      .then((response) => {
        // eslint-disable-next-line no-unused-expressions
        response.city && setFieldValue('address_city', response.city || '');
        const internationalPhone: string = getInternationalPhone(
          PhoneCode[response.country_code],
          ''
        );
        // eslint-disable-next-line no-unused-expressions
        response.country_code && setFieldValue('phone', internationalPhone || '');
        // eslint-disable-next-line no-unused-expressions
        response.country_name && setFieldValue('country', response.country_name || '');
      })
      .catch(() => {});
  }, [resetCandidateCreate]);
  const withFormId = key => `${formId || formName}-${key}`;
  const handlePhoneChange = (value, data, event, formattedValue) => {
    setFieldValue('phone', formattedValue);
    if (data.countryCode) {
      setFieldValue('phone_code', data.countryCode.toUpperCase());
    }
    if (!values.country) {
      setFieldValue('country', data.name);
    }
  };

  const [validateMessage, setValidateMessage] = useState(false);
  useEffect(() => {
    if (phone(values.phone).isValid) {
      setValidateMessage(false);
    }
  }, [values.phone]);

  const isAnyValueEmpty: boolean = mandatoryFields.some(fieldName => isEmpty(values[fieldName]));
  return (
    <StyledMiniFlowWrapper
      finderForm="FinderForm"
      formName={formName}
      domain={curefyDomain}
    >
      <StyledFormHeading>
        {t('Bookfreeconsultation')}
      </StyledFormHeading>
      <StyledFormGrid treatmentFormName={formName}>
        <Grid>
          <StyledContentTop>
            <StyledItemSpacing>
              <StyledRadioGroup domain={curefyDomain}>
                <Field
                  name="gender"
                  formValue="male"
                  id={withFormId('male')}
                  component={ButtonRadio}
                  label={t('Doctors:Mr')}
                />
                <Field
                  name="gender"
                  formValue="female"
                  id={withFormId('female')}
                  component={ButtonRadio}
                  label={t('Doctors:Ms')}
                />
              </StyledRadioGroup>
            </StyledItemSpacing>
            <StyledName>
              <StyledFirstName treatmentFormName={formName}>
                <StyledLabelNew
                  htmlFor={withFormId('first_name')}
                  finderForm={formName === 'FinderForm'}
                  flowForm={formName === 'MiniFlowForm'}
                  treatmentFormName={formName}
                >
                  {`${t('FirstName')}*`}
                </StyledLabelNew>
                <FastField
                  name="first_name"
                  id={withFormId('first_name')}
                  component={Input}
                  inputComponent={StyledInput}
                />
              </StyledFirstName>
              <StyledLastName treatmentFormName={formName}>
                <StyledLabelNew
                  treatmentFormName={formName}
                  htmlFor={withFormId('last_name')}
                  finderForm={formName === 'FinderForm'}
                  flowForm={formName === 'MiniFlowForm'}
                >
                  {`${t('LastName')}*`}
                </StyledLabelNew>
                <FastField
                  name="last_name"
                  id={withFormId('last_name')}
                  component={Input}
                  inputComponent={StyledInput}
                />
              </StyledLastName>
            </StyledName>
            <StyledPhone>
              <StyledLabelNew
                treatmentFormName={formName}
                finderForm={formName === 'FinderForm'}
                flowForm={formName === 'MiniFlowForm'}
              >
                {`${t('Phone')}*`}
              </StyledLabelNew>
              <FastField name="phone">
                {({ field }) => <InputPhone field={field} onChange={handlePhoneChange} />}
              </FastField>
              <StyledValidationMessage ValidationMessage={validateMessage}>
                {t('ValidationMessage')}
              </StyledValidationMessage>
            </StyledPhone>
            <StyledItem>
              <TermsAndConditionsWrapper treatmentFormName={formName}>
                <Checkbox
                  checked={checkboxChecked}
                  id={withFormId('checkboxTermsAndConditions')}
                  onChange={setCheckboxChecked}
                  treatmentFormName={formName}
                  formDoctor={formDoctor}
                />
                <StyledLabelTextWrapper
                  treatmentFormName={formName}
                >
                  <Trans i18nKey="ContactPage:ClickTermsAndConditions" />
                </StyledLabelTextWrapper>
              </TermsAndConditionsWrapper>
            </StyledItem>

            <StyledButtonSubmit
              type="button"
              treatmentFormName={formName}
              disabled={!checkboxChecked
                 || isAnyValueEmpty
                 || sending
                 || !(values.phone && values.phone.length > 6)}
              isModal={isModal}
              onClick={() => {
                if (phone(values.phone).isValid) {
                  handleSubmit();
                } else {
                  setValidateMessage(true);
                }
              }}
            >
              {t('Submit')}
            </StyledButtonSubmit>

          </StyledContentTop>
        </Grid>
      </StyledFormGrid>
    </StyledMiniFlowWrapper>
  );
};


const handleSubmit = (
  formValues,
  {
    props: {
      createSecondOpinionPatient,
      booking_currency,
      booking_category,
      i18n: { language },
      department,
      doctor_id
    }
  }
): void => {
  createSecondOpinionPatient({
    ...formValues,
    doctor_id,
    department,
    booking_currency,
    booking_category: booking_category || 'second-opinion',
    language,
    source: 'Website Flow'
  });
};

const mapStateToProps = (
  {
    candidateCreate: {
      sending, created, error, created_patient_id, modalCreated
    }
  }: GlobalState,
  props: any
) => ({
  sending,
  created,
  modalCreated,
  error,
  created_patient_id,
  initialFormValues: {
    data: {
      attributes: {
        ...loadFormValues('DoctorForm'),
        treatment: props.treatment || '',
        password: props.password || '',
        address_city: props.addressCity || ''
      }
    }
  },
  doctor_id: props.doctor_id
});

const optionalFields = ['gender'];
const mandatoryFields = ['first_name', 'last_name', 'phone', 'phone_code'];
const fieldNames = [...optionalFields, ...mandatoryFields];

const mapDispatchToProps = {
  createSecondOpinionPatient,
  resetCandidateCreate
};

const FormDoctor = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withTranslation('Doctors'),
  withFormik({
    handleSubmit,
    fieldNames,
    reduxErrorKey: 'error',
    reduxInitialValuesKey: 'initialFormValues'
  })
)(ConsultationForm);

FormDoctor.getInitialProps = async ({ query }) => ({
  namespacesRequired: ['Doctors'],
  query
});

export default FormDoctor;
